import React, { memo, useMemo, useState, useCallback } from 'react';
// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
import { graphql, useFragment, useMutation, ConnectionHandler } from 'react-relay';
import type {
	NullableOption,
	LinkRelationshipTypeOption,
} from '@jira/issue-field-link-relationship-editview-full/src/types.tsx';
import RelationshipTypeEditViewEntryPoint from '@jira/issue-field-link-relationship-editview-full/entrypoint.tsx';
import { LinkRelationshipTypeReadView } from '@jira/issue-field-link-relationship-readview-full/src/ui/index.tsx';
import { ModalTransition } from '@atlaskit/modal-dialog';
import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import { useFlagService, toFlagId } from '@atlassian/jira-flags';
import { FieldInlineEditLiteWithEntryPoint } from '@atlassian/jira-issue-field-inline-edit-lite/src/ui/index.tsx';
import { useFieldInlineEditActions } from '@atlassian/jira-issue-field-inline-edit-actions/src/controllers/index.tsx';
import type { ValidationFieldProps } from '@atlassian/jira-issue-field-validation-popup/src/ui/types.tsx';
import { getUpdateAnalyticsFlowHelper } from '@atlassian/jira-issue-analytics/src/services/update-issue-field/index.tsx';
import type { OnSubmitCallbacks } from '@atlassian/jira-issue-field-inline-edit-actions/src/controllers/types.tsx';
import { MutationError } from '@atlassian/jira-mutation-error/src/index.tsx';
import ShortcutScope from '@atlassian/jira-common-components-keyboard-shortcuts/src/shortcut-scope.tsx';
import { useIntl } from '@atlassian/jira-intl';
import { ValidationError } from '@atlassian/jira-fetch/src/utils/errors.tsx';
import { fireOperationalAnalytics } from '@atlassian/jira-product-analytics-bridge/src/utils/fire-analytics.tsx';
import type { MutationErrorPayload } from '@atlassian/jira-mutation-error/src/types.tsx';
import { useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import { createSelectedOptionFromFieldData } from '../utils.tsx';
import messages from '../messages.tsx';
import type { ui_IssueFieldLinkRelationshipInlineEditFull_LinkRelationshipInlineEditView_issueRef$key } from './__generated__/ui_IssueFieldLinkRelationshipInlineEditFull_LinkRelationshipInlineEditView_issueRef.graphql';
import type {
	ui_JiraIssueFieldLinkRelationshipInlineEditFull_LinkRelationshipTypeCell_Mutation,
	ui_JiraIssueFieldLinkRelationshipInlineEditFull_LinkRelationshipTypeCell_Mutation$data,
} from './__generated__/ui_JiraIssueFieldLinkRelationshipInlineEditFull_LinkRelationshipTypeCell_Mutation.graphql';
import type { ui_IssueFieldLinkRelationshipInlineEditFull_LinkRelationshipInlineEditView_fieldRef$key } from './__generated__/ui_IssueFieldLinkRelationshipInlineEditFull_LinkRelationshipInlineEditView_fieldRef.graphql';
import ConfirmationDialog from './confirmation-dialog/index.tsx';

export type Props = {
	fieldRef: ui_IssueFieldLinkRelationshipInlineEditFull_LinkRelationshipInlineEditView_fieldRef$key;
	issueRef: ui_IssueFieldLinkRelationshipInlineEditFull_LinkRelationshipInlineEditView_issueRef$key;
	issueStoreId: string; // Relay store ID for the issue row
	itemsConnectionId: string; // Connection ID for the items list
	isEditable: boolean;
	onIssueUnlink?: (issueKey: string) => void;
};

const isMutationErrorPayload = (
	error: Error | MutationErrorPayload | undefined,
): error is MutationErrorPayload =>
	typeof error === 'object' && error !== null && 'extensions' in error && Boolean(error.extensions);

const isEqualValue = (a: NullableOption, b: NullableOption) => a?.id === b?.id;

export const LinkRelationshipInlineEditView = memo<Props>(
	({ fieldRef, issueRef, onIssueUnlink, issueStoreId, itemsConnectionId, isEditable }) => {
		const [showUnlinkConfirmation, setShowUnlinkConfirmation] = useState(false);
		const { showFlag } = useFlagService();
		const { formatMessage } = useIntl();
		const { createAnalyticsEvent } = useAnalyticsEvents();

		const issueData =
			useFragment<ui_IssueFieldLinkRelationshipInlineEditFull_LinkRelationshipInlineEditView_issueRef$key>(
				// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
				graphql`
					fragment ui_IssueFieldLinkRelationshipInlineEditFull_LinkRelationshipInlineEditView_issueRef on JiraIssue {
						issueId
						key
						isRemoteIssue
						webUrl
						issueType {
							name
						}
					}
				`,
				issueRef,
			);

		const data =
			useFragment<ui_IssueFieldLinkRelationshipInlineEditFull_LinkRelationshipInlineEditView_fieldRef$key>(
				// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
				graphql`
					fragment ui_IssueFieldLinkRelationshipInlineEditFull_LinkRelationshipInlineEditView_fieldRef on JiraIssueLinkRelationshipTypeField {
						id
						fieldId
						type
						name
						issueLink {
							id
							direction
							relationName
							type {
								id
							}
						}
						...ui_IssueFieldLinkRelationshipReadviewFull_LinkRelationshipTypeReadView
					}
				`,
				fieldRef,
			);

		const initialValue = useMemo(() => {
			const issueLinkData = {
				issueLinkTypeARI: data?.issueLink?.type?.id ?? '',
				relationName: data?.issueLink?.relationName ?? '',
				direction: data?.issueLink?.direction ?? 'INWARD',
			};

			return createSelectedOptionFromFieldData(issueLinkData);
		}, [data?.issueLink]);
		const [selectedOption, setSelectedOption] = useState<NullableOption>(initialValue);

		const [commit] =
			useMutation<ui_JiraIssueFieldLinkRelationshipInlineEditFull_LinkRelationshipTypeCell_Mutation>(
				// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
				graphql`
					mutation ui_JiraIssueFieldLinkRelationshipInlineEditFull_LinkRelationshipTypeCell_Mutation(
						$input: JiraUpdateIssueLinkRelationshipTypeFieldInput!
					) @raw_response_type {
						jira @optIn(to: ["JiraIssueFieldMutations"]) {
							updateIssueLinkRelationshipTypeField(input: $input) {
								success
								errors {
									message
									extensions {
										statusCode
										errorType
									}
								}
								field {
									id
									issueLink {
										id
										direction
										relationName
										type {
											id
										}
									}
								}
							}
						}
					}
				`,
			);

		const issueId = issueData?.issueId ?? '';
		const issueKey = issueData?.key ?? '';
		const workType = issueData?.issueType?.name ?? '';
		const currentIssueFieldARI = data?.id || '';
		const currentIssueLinkARI = data?.issueLink?.id || '';
		const handleSubmit = useCallback(
			(option: NullableOption, { onSuccess, onFail }: OnSubmitCallbacks) => {
				const nextDirection = option?.direction;
				const nextRelationName = option?.label;
				const nextIssueLinkTypeARI = option?.value;

				if (!nextIssueLinkTypeARI) {
					onFail(new Error('No issue link type id provided'));
					return;
				}

				commit({
					variables: {
						input: {
							id: currentIssueFieldARI,
							operation: {
								operation: 'SET',
								update: {
									issueLinkId: currentIssueLinkARI,
									issueLinkTypeId: nextIssueLinkTypeARI,
									direction: nextDirection,
								},
							},
						},
					},
					onCompleted: (mutationData) => {
						const mutationResponse = mutationData.jira?.updateIssueLinkRelationshipTypeField;
						if (mutationResponse?.success) {
							onSuccess();
							fireOperationalAnalytics(createAnalyticsEvent({}), 'ui taskSuccess', {
								task: 'linkRelationshipTypeUpdate',
								linkedWorkItemId: issueId,
							});
						} else {
							const mutationError = mutationResponse?.errors?.[0];
							const message =
								mutationError?.message ?? 'Failed to update issue link relationship type field';
							const statusCode = mutationError?.extensions?.statusCode ?? 0;
							if (statusCode >= 400 && statusCode < 500) {
								onFail(new ValidationError(message, [], statusCode));
							} else {
								onFail(new MutationError(mutationError ?? { message }));
							}

							fireOperationalAnalytics(createAnalyticsEvent({}), 'ui taskFail', {
								task: 'linkRelationshipTypeUpdate',
								errorMessage: message,
								errorType: mutationError?.extensions?.errorType,
								statusCode,
								linkedWorkItemId: issueId,
							});
						}
					},
					onError(error) {
						fireOperationalAnalytics(createAnalyticsEvent({}), 'ui taskFail', {
							task: 'linkRelationshipTypeUpdate',
							errorMessage: error?.message ?? 'Failed to update issue link relationship type field',
							linkedWorkItemId: issueId,
						});

						onFail(error);
					},
					optimisticResponse: {
						jira: {
							updateIssueLinkRelationshipTypeField: {
								success: true,
								errors: null,
								field: {
									id: currentIssueFieldARI,
									issueLink: {
										id: currentIssueLinkARI,
										direction: nextDirection,
										relationName: nextRelationName,
										type: {
											id: nextIssueLinkTypeARI,
										},
									},
								},
							},
						},
					},
				});
			},
			[commit, currentIssueFieldARI, currentIssueLinkARI, createAnalyticsEvent, issueId],
		);

		const fieldId = data?.fieldId;
		const setAnalyticsAttributes = useCallback(
			(value: NullableOption) => {
				getUpdateAnalyticsFlowHelper().setActionTakenAttributes(
					fieldId,
					`${initialValue?.value}_${initialValue?.direction}`,
					`${value?.value}_${value?.direction}`,
				);
			},
			[fieldId, initialValue],
		);

		const {
			hasServerValidationError,
			handleCancel,
			handleConfirm,
			handleChangeAndConfirm,
			handleEdit,
			invalidMessage,
			isEditing,
		} = useFieldInlineEditActions<NullableOption, undefined>({
			fieldId: data?.fieldId,
			fieldName: data?.name,
			fieldType: data?.type,
			initialValue,
			updatedValue: selectedOption,
			isValueEqual: isEqualValue,
			clipboardComponentType: undefined,
			onSubmit: handleSubmit,
			onUpdateValue: setSelectedOption,
			setAnalyticsAttributes,
		});

		const handleUnlinkSuccess = useCallback(
			(analyticsEvent: UIAnalyticsEvent) => {
				showFlag({
					id: toFlagId('unlink-success'),
					type: 'success',
					messageId:
						'issue-field-link-relationship-inline-edit-full.ui.show-flag.success.issue-cells.link-relationship-type.show-flag.success.unlink-success',
					messageType: 'transactional',
					title: formatMessage(messages.unlinkSuccessTitle, {
						workType,
						issueKey,
					}),
					isAutoDismiss: true,
					actions: [
						{
							content: formatMessage(messages.viewCta),
							href: issueData?.isRemoteIssue ? (issueData?.webUrl ?? '') : `/browse/${issueKey}`,
							target: issueData?.isRemoteIssue ? '_blank' : undefined,
						},
					],
				});

				getUpdateAnalyticsFlowHelper().fireAnalyticsEnd(
					data?.type,
					{
						analytics: analyticsEvent,
						attributes: {
							fieldType: data?.type,
							actionTaken: 'remove',
							issueId,
						},
					},
					false,
				);

				fireOperationalAnalytics(analyticsEvent, 'ui taskSuccess', {
					task: 'linkRelationshipTypeUnlink',
					linkedWorkItemId: issueId,
				});

				onIssueUnlink?.(issueKey);
			},
			[
				issueKey,
				showFlag,
				formatMessage,
				onIssueUnlink,
				data?.type,
				issueId,
				issueData?.isRemoteIssue,
				issueData?.webUrl,
				workType,
			],
		);

		const handleUnlinkError = useCallback(
			(error: Error | MutationErrorPayload | undefined, analyticsEvent: UIAnalyticsEvent) => {
				showFlag({
					id: toFlagId('unlink-error'),
					type: 'error',
					messageId:
						'issue-field-link-relationship-inline-edit-full.ui.show-flag.error.issue-cells.link-relationship-type.show-flag.error.unlink-error',
					messageType: 'transactional',
					title: formatMessage(messages.unlinkErrorTitle, {
						workType: workType.toLowerCase(),
						issueKey,
					}),
					description: formatMessage(messages.unlinkErrorDescription),
					isAutoDismiss: true,
					actions: [
						{
							content: formatMessage(messages.refreshCta),
							onClick: () => globalThis.location.reload(),
						},
					],
				});

				let attributes: Record<string, unknown> = {
					errorMessage: error?.message ?? 'Failed to unlink work item',
					linkedWorkItemId: issueId,
				};
				if (isMutationErrorPayload(error)) {
					attributes = {
						...attributes,
						statusCode: error.extensions?.statusCode,
						errorType: error.extensions?.errorType,
					};
				}

				fireOperationalAnalytics(analyticsEvent, 'ui taskFail', {
					task: 'linkRelationshipTypeUnlink',
					...attributes,
				});
			},
			[showFlag, formatMessage, workType, issueKey, issueId],
		);

		const handleUnlinkSubmit = useCallback(
			(analyticsEvent: UIAnalyticsEvent) => {
				setShowUnlinkConfirmation(false);

				commit({
					variables: {
						input: {
							id: currentIssueFieldARI,
							operation: {
								operation: 'SET',
								update: {
									issueLinkId: currentIssueLinkARI,
									issueLinkTypeId: null,
								},
							},
						},
					},
					optimisticUpdater: (store) => {
						// Optimistically remove the issue row from the table using ConnectionHandler
						// This follows the same pattern as the hide done functionality
						if (itemsConnectionId && issueStoreId) {
							const connectionRecord = store.get(itemsConnectionId);
							if (connectionRecord) {
								ConnectionHandler.deleteNode(connectionRecord, issueStoreId);
							}
						}
					},
					updater: (store) => {
						// Ensure the row stays removed after the server response
						// This prevents the server response from re-adding the row
						if (itemsConnectionId && issueStoreId) {
							const connectionRecord = store.get(itemsConnectionId);
							if (connectionRecord) {
								// Check if the node still exists in the connection
								const edges = connectionRecord.getLinkedRecords('edges');
								const nodeExists = edges?.some((edge) => {
									const node = edge?.getLinkedRecord('node');
									return node?.getDataID() === issueStoreId;
								});

								// If the node was re-added by the server response, remove it again
								if (nodeExists) {
									ConnectionHandler.deleteNode(connectionRecord, issueStoreId);
								}
							}
						}
					},
					onCompleted: (
						mutationData: ui_JiraIssueFieldLinkRelationshipInlineEditFull_LinkRelationshipTypeCell_Mutation$data,
					) => {
						const mutationResponse = mutationData?.jira?.updateIssueLinkRelationshipTypeField;
						if (mutationResponse?.success) {
							handleUnlinkSuccess(analyticsEvent);
						} else {
							const mutationError = mutationResponse?.errors?.[0];
							handleUnlinkError(mutationError, analyticsEvent);
						}
					},
					onError: (error) => handleUnlinkError(error, analyticsEvent),
				});
			},
			[
				commit,
				currentIssueFieldARI,
				currentIssueLinkARI,
				handleUnlinkSuccess,
				handleUnlinkError,
				issueStoreId,
				itemsConnectionId,
			],
		);

		const handleUnlinkCancel = useCallback(() => {
			setShowUnlinkConfirmation(false);
		}, []);

		const handleUnlink = useCallback(
			(analyticsEvent: UIAnalyticsEvent) => {
				handleCancel(analyticsEvent);
				setShowUnlinkConfirmation(true);
			},
			[handleCancel],
		);

		const getEditViewProps = useCallback(
			(fieldProps: ValidationFieldProps) => ({
				...fieldProps,
				selectedOption,
				isDisabled: !isEditable,
				onChange: (value: LinkRelationshipTypeOption) => {
					handleChangeAndConfirm(value);
				},
				onUnlink: handleUnlink,
			}),
			[handleChangeAndConfirm, handleUnlink, isEditable, selectedOption],
		);

		const readView = useCallback(() => <LinkRelationshipTypeReadView fieldRef={data} />, [data]);

		return (
			<>
				<FieldInlineEditLiteWithEntryPoint
					editViewPopup
					editViewPopupAlignBlock="center"
					editViewPopupMinWidth="small"
					editViewEntryPoint={RelationshipTypeEditViewEntryPoint}
					editViewEntryPointParams={{}}
					getEditViewProps={getEditViewProps}
					fieldName={data?.name}
					isEditable={isEditable}
					readView={readView}
					invalidMessage={invalidMessage}
					isEditing={isEditing}
					hasUnsubmittedChanges={hasServerValidationError}
					onEdit={handleEdit}
					onCancel={handleCancel}
					onConfirm={handleConfirm}
					readViewFitContainerHeight
					hideActionButtons
				/>
				{showUnlinkConfirmation && (
					<ModalTransition>
						<ShortcutScope>
							<ConfirmationDialog
								issueKey={issueKey}
								issueId={issueId}
								workType={workType}
								onUnlinkCancel={handleUnlinkCancel}
								onUnlinkSubmit={handleUnlinkSubmit}
							/>
						</ShortcutScope>
					</ModalTransition>
				)}
			</>
		);
	},
);
